import { giphyConfig } from '@/src/config/env';

const GIPHY_API_BASE_URL = 'https://api.giphy.com/v1/gifs';
const DEFAULT_LIMIT = 24;

type GiphyImage = {
  height?: string;
  size?: string;
  url?: string;
  webp?: string;
  width?: string;
};

type GiphyApiGif = {
  id?: string;
  title?: string;
  url?: string;
  images?: {
    fixed_width?: GiphyImage;
    fixed_width_downsampled?: GiphyImage;
    original?: GiphyImage;
    preview_gif?: GiphyImage;
  };
};

type GiphyApiResponse = {
  data?: GiphyApiGif[];
};

export type GiphyGif = {
  giphyId: string;
  title: string;
  pageURL: string;
  previewURL: string;
  originalURL: string;
  width: number;
  height: number;
};

function assertGiphyConfigured() {
  if (!giphyConfig.apiKey) {
    throw new Error(
      'GIPHY is not configured yet. Add EXPO_PUBLIC_GIPHY_API_KEY to .env and rebuild the app.',
    );
  }
}

function parseNumber(value: string | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseGif(gif: GiphyApiGif): GiphyGif | null {
  const originalURL =
    gif.images?.fixed_width?.url ??
    gif.images?.original?.url ??
    gif.images?.fixed_width_downsampled?.url;
  const previewURL =
    gif.images?.fixed_width_downsampled?.url ??
    gif.images?.preview_gif?.url ??
    originalURL;
  const width =
    parseNumber(gif.images?.fixed_width?.width) ||
    parseNumber(gif.images?.original?.width);
  const height =
    parseNumber(gif.images?.fixed_width?.height) ||
    parseNumber(gif.images?.original?.height);

  if (!gif.id || !originalURL || !previewURL) {
    return null;
  }

  return {
    giphyId: gif.id,
    title: gif.title ?? 'GIPHY GIF',
    pageURL: gif.url ?? `https://giphy.com/gifs/${gif.id}`,
    previewURL,
    originalURL,
    width,
    height,
  };
}

async function requestGiphy(
  endpoint: 'search' | 'trending',
  params: Record<string, string | number | undefined>,
) {
  assertGiphyConfigured();

  const url = new URL(`${GIPHY_API_BASE_URL}/${endpoint}`);
  url.searchParams.set('api_key', giphyConfig.apiKey);
  url.searchParams.set('rating', giphyConfig.rating);
  url.searchParams.set('limit', String(params.limit ?? DEFAULT_LIMIT));

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && key !== 'limit') {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error('Unable to load GIFs from GIPHY. Please try again.');
  }

  const payload = (await response.json()) as GiphyApiResponse;

  return (payload.data ?? [])
    .map(parseGif)
    .filter((gif): gif is GiphyGif => gif !== null);
}

export function getTrendingGifs(limit = DEFAULT_LIMIT) {
  return requestGiphy('trending', { limit });
}

export function searchGifs(query: string, limit = DEFAULT_LIMIT) {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return getTrendingGifs(limit);
  }

  return requestGiphy('search', {
    limit,
    lang: 'en',
    q: trimmedQuery.slice(0, 50),
  });
}
